import { isEqual } from 'lodash-es'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { v4 as uuid } from 'uuid'
import {
    AdminPromotion,
    AdminScheduledPromotion,
    ApiPromotion,
    DEFAULT_ACCOUNT_TIME_ZONE,
    GlobalPromotionMode,
    AdminClientSettings,
    UpsertScheduledPromotionRequest,
} from '@storefronts/common-types'
import { useSettings } from '@/components/settings/SettingsProvider'
import { R } from '@/modules/api/actions'
import { HashedPromotion } from '@/components/promotions/types'
import {
    defaultFromSettings,
    hashedFromSpecific,
} from '@/components/promotions/schedule/conversions'
import { globalModeFromDefault } from '@/components/promotions/schedule/defaultPromotion'
import { applyOtherPatch } from '@/components/promotions/schedule/overlap'
import { upsertScheduleEntry } from '@/components/promotions/schedule/scheduleForm'
import {
    DefaultPromotion,
    ScheduleEntry,
} from '@/components/promotions/schedule/types'

export interface UsePromotionsReturn {
    promotions: HashedPromotion[]
    setPromotions: React.Dispatch<React.SetStateAction<HashedPromotion[]>>
    globalPromotionHash: string | null
    setGlobalPromotionHash: React.Dispatch<React.SetStateAction<string | null>>
    globalPromotion: HashedPromotion | null
    globalPromotionMode: GlobalPromotionMode
    setGlobalPromotionMode: React.Dispatch<
        React.SetStateAction<GlobalPromotionMode>
    >
    adminPromotion: AdminPromotion | null
    scheduleEntries: ScheduleEntry[]
    defaultPromotion: DefaultPromotion
    applyDefaultPromotion: (next: DefaultPromotion) => void
    saveScheduleEntry: (entry: ScheduleEntry) => Promise<boolean>
    adjustScheduleEntry: (
        otherId: string,
        patch: { startsAt?: string; endsAt?: string },
    ) => Promise<void>
    removeScheduleEntry: (entry: ScheduleEntry) => Promise<void>
    timeZone: string
    pendingBannerText: string | null
    setPendingBannerText: React.Dispatch<React.SetStateAction<string | null>>
    currentPromotion: HashedPromotion
    setCurrentPromotion: React.Dispatch<React.SetStateAction<HashedPromotion>>
    handleSave: () => Promise<void>
    discardChanges: () => void
    discardPromotion: (promotionHash: string) => Promise<void>
    isLoading: boolean
    hasChanges: boolean
    isEditing: boolean
    setIsEditing: React.Dispatch<React.SetStateAction<boolean>>
    dialogOpen: boolean
    setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export const usePromotions = (): UsePromotionsReturn => {
    const { settings, setSettings, refreshSettings } = useSettings()
    const promotionSyncKey = useRef<string | null>(null)
    const [promotions, setPromotions] = useState<HashedPromotion[]>([])
    const [originalPromotions, setOriginalPromotions] = useState<
        HashedPromotion[]
    >([])
    const [globalPromotionHash, setGlobalPromotionHash] = useState<
        string | null
    >(null)
    const [originalGlobalPromotionHash, setOriginalGlobalPromotionHash] =
        useState<string | null>(null)
    const [globalPromotionMode, setGlobalPromotionMode] =
        useState<GlobalPromotionMode>('MANUAL')
    const [originalGlobalPromotionMode, setOriginalGlobalPromotionMode] =
        useState<GlobalPromotionMode>('MANUAL')
    const [scheduleEntries, setScheduleEntries] = useState<ScheduleEntry[]>([])
    const [defaultPromotion, setDefaultPromotion] = useState<DefaultPromotion>({
        kind: 'AUTO_SYNC',
    })

    const [currentPromotion, setCurrentPromotion] = useState<HashedPromotion>({
        name: '',
        bannerText: '',
        discountCode: '',
        discountType: 'PERCENT',
        discountValue: 0,
        hash: uuid(),
        combinesWithOrderDiscounts: false,
        combinesWithProductDiscounts: false,
        combinesWithShippingDiscounts: false,
    })

    const [pendingBannerText, setPendingBannerText] = useState<string | null>(
        settings?.adminPromotion?.bannerText ?? null,
    )
    const [originalBannerText, setOriginalBannerText] = useState<string | null>(
        settings?.adminPromotion?.bannerText ?? null,
    )

    const isLoading = !settings
    const [isEditing, setIsEditing] = useState(false)
    const [dialogOpen, setDialogOpen] = useState(false)

    const globalPromotion =
        promotions.find((p) => p.hash === globalPromotionHash) || null

    const transformPromotionToHashedPromotion = (
        promotion: AdminPromotion,
    ): HashedPromotion => {
        return {
            ...promotion,
            hash: uuid(),
        }
    }

    useEffect(() => {
        if (!settings) return
        setScheduleEntries(
            (settings.scheduledPromotions ?? []).map(toScheduleEntry),
        )
        const syncKey = [
            settings.updatedAt,
            settings.globalPromotionMode,
            settings.globalPromotion?.id ?? '',
            settings.promotions
                .map((promotion) => `${promotion.id ?? ''}:${promotion.name}`)
                .join(','),
            settings.adminPromotion?.bannerText ?? '',
        ].join('|')
        if (promotionSyncKey.current === syncKey) return
        promotionSyncKey.current = syncKey

        const hashedPromotions = settings.promotions.map(
            transformPromotionToHashedPromotion,
        )
        setOriginalPromotions(hashedPromotions)
        setPromotions(hashedPromotions)

        const originalHash =
            hashedPromotions.find(
                (hp) => hp.id === settings.globalPromotion?.id,
            )?.hash || null
        setOriginalGlobalPromotionHash(originalHash)
        setGlobalPromotionHash(originalHash)
        const selected =
            hashedPromotions.find((hp) => hp.hash === originalHash) ?? null
        if (settings.globalPromotionMode != null) {
            setOriginalGlobalPromotionMode(settings.globalPromotionMode)
            setGlobalPromotionMode(settings.globalPromotionMode)
            setDefaultPromotion(
                defaultFromSettings(settings.globalPromotionMode, selected),
            )
        }
        setPendingBannerText(settings.adminPromotion?.bannerText ?? null)
        setOriginalBannerText(settings.adminPromotion?.bannerText ?? null)
    }, [settings])

    const promotionsChanged =
        globalPromotionHash !== originalGlobalPromotionHash ||
        !isEqual(promotions, originalPromotions)

    const modeChanged = globalPromotionMode !== originalGlobalPromotionMode
    const bannerTextChanged = pendingBannerText !== originalBannerText
    const hasChanges = promotionsChanged || modeChanged || bannerTextChanged

    const apiPromotionEqualsHashedPromotion = (
        a: ApiPromotion,
        b: HashedPromotion,
    ): boolean => {
        return (
            a.name === b.name &&
            sameText(a.bannerText, b.bannerText) &&
            sameText(a.discountCode, b.discountCode) &&
            a.discountType === b.discountType &&
            a.discountValue === b.discountValue
        )
    }

    const handleSave = async () => {
        if (!hasChanges) return

        const isManualMode =
            globalPromotionMode === 'MANUAL' ||
            globalPromotionMode === 'MANUAL_WITH_FALLBACK'
        const clientSettingsPayload: Partial<AdminClientSettings> = {}
        if (promotionsChanged) {
            const response = await R.bulkReplacePromotions({
                promotions: promotions,
            })
            if (response.isError) return

            if (!isManualMode || !globalPromotion) {
                clientSettingsPayload.globalPromotion = null
            } else {
                const updated = (response.result?.promotions ?? []).find((up) =>
                    apiPromotionEqualsHashedPromotion(up, globalPromotion),
                )
                // Only set when we found a match; omit when match fails so we don't clear saved promotion
                if (updated) clientSettingsPayload.globalPromotion = updated
            }
        }

        if (globalPromotionMode !== originalGlobalPromotionMode) {
            clientSettingsPayload.globalPromotionMode = globalPromotionMode

            if (!isManualMode) {
                clientSettingsPayload.globalPromotion = null
            }
        }

        if (settings?.adminPromotion && bannerTextChanged) {
            clientSettingsPayload.adminPromotion = {
                ...settings.adminPromotion,
                bannerText: pendingBannerText,
            }
        }

        await R.updateClientSettings(clientSettingsPayload)

        await refreshSettings()
    }

    const applyDefaultPromotion = (next: DefaultPromotion) => {
        setDefaultPromotion(next)
        setGlobalPromotionMode(globalModeFromDefault(next))
        if (next.kind !== 'SPECIFIC') {
            setGlobalPromotionHash(null)
            return
        }
        const hashed = hashedFromSpecific(next.promotion)
        setPromotions((prev) => {
            const exists = prev.some((item) => item.hash === hashed.hash)
            if (exists) {
                return prev.map((item) =>
                    item.hash === hashed.hash
                        ? {
                              ...item,
                              name: hashed.name,
                              bannerText: hashed.bannerText,
                              discountCode: hashed.discountCode,
                              discountType: hashed.discountType,
                              discountValue: hashed.discountValue,
                          }
                        : item,
                )
            }
            return [...prev, hashed]
        })
        setGlobalPromotionHash(hashed.hash)
    }

    const saveScheduleEntry = async (
        entry: ScheduleEntry,
    ): Promise<boolean> => {
        const id = persistedScheduleId(entry.id)
        const request = toScheduledRequest(entry)
        const response =
            id == null
                ? await R.createScheduledPromotion(request)
                : await R.updateScheduledPromotion(id, request)
        if (response.isError) {
            toast.error(response.message)
            return false
        }
        const savedRow = response.result.scheduledPromotion
        const saved = toScheduleEntry(savedRow)
        setScheduleEntries((prev) =>
            upsertScheduleEntry(
                prev.filter((item) => item.id !== entry.id),
                saved,
            ),
        )
        setSettings((prev) =>
            prev ? replaceScheduledPromotion(prev, savedRow) : prev,
        )
        return true
    }

    const adjustScheduleEntry = async (
        otherId: string,
        patch: { startsAt?: string; endsAt?: string },
    ) => {
        const current = scheduleEntries.find((entry) => entry.id === otherId)
        if (!current) return
        const next: ScheduleEntry = {
            ...current,
            startsAt: patch.startsAt ?? current.startsAt,
            endsAt: patch.endsAt ?? current.endsAt,
        }
        setScheduleEntries((prev) => applyOtherPatch(prev, otherId, patch))
        const saved = await saveScheduleEntry(next)
        if (!saved) {
            setScheduleEntries((prev) => upsertScheduleEntry(prev, current))
        }
    }

    const removeScheduleEntry = async (entry: ScheduleEntry) => {
        const id = persistedScheduleId(entry.id)
        if (id == null) {
            setScheduleEntries((prev) =>
                prev.filter((item) => item.id !== entry.id),
            )
            return
        }
        const response = await R.deleteScheduledPromotion(id)
        if (response.isError) {
            toast.error(response.message)
            return
        }
        setScheduleEntries((prev) =>
            prev.filter((item) => item.id !== entry.id),
        )
        setSettings((prev) =>
            prev
                ? {
                      ...prev,
                      scheduledPromotions: (
                          prev.scheduledPromotions ?? []
                      ).filter((item) => item.id !== id),
                  }
                : prev,
        )
    }

    const discardChanges = () => {
        setPromotions(originalPromotions)
        setGlobalPromotionHash(originalGlobalPromotionHash)
        setGlobalPromotionMode(originalGlobalPromotionMode)
        setPendingBannerText(originalBannerText)
        const selected =
            originalPromotions.find(
                (item) => item.hash === originalGlobalPromotionHash,
            ) ?? null
        setDefaultPromotion(
            defaultFromSettings(originalGlobalPromotionMode, selected),
        )
    }

    /**
     * Deletes a single promotion without affecting other unsaved changes.
     * Use case: dismissing warnings about expired or deleted promotions.
     *
     * @param promotionHash - Hash of the promotion row to remove.
     */
    const discardPromotion = async (promotionHash: string): Promise<void> => {
        const target = promotions.find((p) => p.hash === promotionHash)
        if (!target) return

        const previousPromotions = promotions
        const nextPromotions = previousPromotions.filter(
            (p) => p.hash !== promotionHash,
        )

        setPromotions(nextPromotions)

        if (target.id == null) {
            return
        }

        const response = await R.deletePromotion(target.id)
        if (response.isError) {
            setPromotions(previousPromotions)
            return
        }

        setOriginalPromotions((orig) =>
            orig.filter((p) => p.hash !== promotionHash),
        )
    }

    const adminPromotion = settings?.adminPromotion ?? null

    return {
        promotions,
        setPromotions,
        globalPromotionHash,
        setGlobalPromotionHash,
        globalPromotion,
        globalPromotionMode,
        setGlobalPromotionMode,
        adminPromotion,
        scheduleEntries,
        defaultPromotion,
        applyDefaultPromotion,
        saveScheduleEntry,
        adjustScheduleEntry,
        removeScheduleEntry,
        timeZone: settings?.timeZone ?? DEFAULT_ACCOUNT_TIME_ZONE,
        pendingBannerText,
        setPendingBannerText,
        currentPromotion,
        setCurrentPromotion,
        handleSave,
        discardChanges,
        discardPromotion,
        isLoading,
        hasChanges,
        isEditing,
        setIsEditing,
        dialogOpen,
        setDialogOpen,
    }
}

function replaceScheduledPromotion(
    settings: AdminClientSettings,
    row: AdminScheduledPromotion,
): AdminClientSettings {
    const scheduledPromotions = [
        ...(settings.scheduledPromotions ?? []).filter(
            (item) => item.id !== row.id,
        ),
        row,
    ].sort((a, b) => a.startsAt.localeCompare(b.startsAt))
    return { ...settings, scheduledPromotions }
}

function toScheduleEntry(row: AdminScheduledPromotion): ScheduleEntry {
    return {
        id: String(row.id),
        name: row.name,
        discountCode: row.discountCode,
        discountType: row.discountType,
        discountValue: row.discountValue,
        bannerText: row.bannerText,
        source: row.source,
        shopifyId: row.shopifyId,
        startsAt: row.startsAt,
        endsAt: row.endsAt,
        copiedShopifyStartsAt: row.copiedShopifyStartsAt,
        copiedShopifyEndsAt: row.copiedShopifyEndsAt,
    }
}

function sameText(
    left: string | null | undefined,
    right: string | null | undefined,
): boolean {
    return (left ?? '') === (right ?? '')
}

function persistedScheduleId(id: string): number | null {
    if (!/^\d+$/.test(id)) return null
    return Number(id)
}

function toScheduledRequest(
    entry: ScheduleEntry,
): UpsertScheduledPromotionRequest {
    return {
        name: entry.name,
        bannerText: entry.bannerText,
        discountCode: entry.discountCode,
        discountValue: entry.discountValue,
        discountType: entry.discountType,
        shopifyId: entry.shopifyId,
        source: entry.source,
        startsAt: entry.startsAt,
        endsAt: entry.endsAt,
        copiedShopifyStartsAt: entry.copiedShopifyStartsAt,
        copiedShopifyEndsAt: entry.copiedShopifyEndsAt,
    }
}
